import { useState, useCallback } from "react";
import {
  Snackbar,
  Alert,
  AlertTitle,
  IconButton,
  AlertColor,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";

export interface IToastConfig {
  message: string;
  title: string;
  type: "info" | "error" | "success" | "warning";
  displayTime: number;
}

export const useToastMessageHandler = () => {
  const [toastConfig, setToastConfig] = useState<IToastConfig | null>(null);

  const handleClose = () => {
    setToastConfig(null);
  };

  const getIcon = (type: "info" | "error" | "success" | "warning") => {
    switch (type) {
      case "success":
        return <CheckCircleIcon />;
      case "error":
        return <ErrorIcon />;
      case "warning":
        return <WarningIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const showToast = useCallback((config: IToastConfig) => {
    setToastConfig(config);
  }, []);

  const ToastMessage = () => {
    const titleColor =
      toastConfig?.type === "error"
        ? "#ffcccc"
        : toastConfig?.type === "success"
          ? "#ccffcc"
          : toastConfig?.type === "warning"
            ? "#fff0b3"
            : "#cce5ff";

    return (
      <Snackbar
        open={toastConfig !== null}
        autoHideDuration={toastConfig?.displayTime}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            marginTop: 2,
            borderRadius: 2,
            boxShadow: 3,
            minWidth: "300px",
          },
        }}
      >
        <Alert
          variant="filled"
          severity={(toastConfig?.type as AlertColor) || "info"}
          icon={toastConfig ? getIcon(toastConfig.type) : undefined}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleClose}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          onClose={handleClose}
          sx={{
            alignItems: "center",
            "& .MuiAlertTitle-root": {
              margin: 0,
              color: titleColor,
            },
          }}
        >
          {toastConfig?.title && <AlertTitle>{toastConfig.title}</AlertTitle>}
          {toastConfig?.message}
        </Alert>
      </Snackbar>
    );
  };

  return {
    setToastConfig,
    ToastMessage,
    showToast,
  };
};
